import React, { useEffect, useMemo, useState, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Download, Share2, X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ContactPickerDialog } from "@/components/shared/ContactPickerDialog";
import { buildContactOptions } from "@/lib/contactActions";

// pdf.js worker (CDN matching installed version)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const sanitizeFilename = (s) =>
  String(s || "")
    .replace(/[\\/:*?"<>|]+/g, "")
    .trim()
    .replace(/\s+/g, "_");

export const buildReceiptFilename = (receipt) => {
  const name = sanitizeFilename(receipt?.snapshot?.recipient?.name || "");
  const num = receipt?.receipt_number || "Receipt";
  return name ? `${name}_${num}.pdf` : `Receipt_${num}.pdf`;
};

export const buildPublicReceiptUrl = (receipt) => {
  const origin = window.location.origin;
  const code = (receipt?.studio_prefix || "STUDIO").toLowerCase();
  return `${origin}/r/${encodeURIComponent(code)}/${receipt?.share_token}`;
};

export const buildPublicPdfUrl = (receipt) => {
  // Public, unauthenticated PDF binary endpoint
  const base = process.env.REACT_APP_BACKEND_URL;
  return `${base}/api/public/receipts/${receipt?.share_token}/pdf`;
};

/** Normalise local 10-digit numbers to international format for WhatsApp. */
const normalisePhoneForWa = (raw, defaultCountryCode) => {
  let s = String(raw || "").replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+")) return s.slice(1); // wa.me wants no leading +
  // strip 00 prefix if present
  if (s.startsWith("00")) return s.slice(2);
  // 10-digit local number → prepend country code
  const cc = String(defaultCountryCode || "").replace(/[^\d]/g, "");
  if (s.length === 10 && cc) return cc + s;
  return s;
};

export const buildWhatsAppShare = ({ receipt, defaultCountryCode, orgDisplayName, overridePhone }) => {
  const recipient = receipt?.snapshot?.recipient || {};
  const rawPhone = overridePhone || recipient.whatsapp_phone || recipient.phone;
  const phone = normalisePhoneForWa(rawPhone, defaultCountryCode);
  const name = recipient.name || "there";
  const num = receipt?.receipt_number || "—";
  const summary = receipt?.snapshot?.payment_summary || {};
  const cur = receipt?.snapshot?.currency_symbol || "₹";
  const txn = summary.transaction_amount;
  const out = summary.outstanding_balance;
  const link = buildPublicReceiptUrl(receipt);
  const orgName = orgDisplayName || receipt?.snapshot?.org?.display_name || receipt?.snapshot?.org?.organization_name || "";

  const text =
`Hello ${name},

Please find your payment receipt below.

Receipt Number: ${num}
Amount Paid: ${cur}${txn}
Outstanding: ${cur}${out}

Download Receipt:
${link}

Thank you,
${orgName}`;

  return {
    phone,
    text,
    waUrl: phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`,
  };
};

/**
 * In-app PDF viewer (pdf.js based) — works in Chrome / Safari / Edge / Android / iOS.
 * Supports zoom, paging, download, WhatsApp share, close.
 */
export const ReceiptViewerModal = ({ open, onOpenChange, receipt, defaultCountryCode = "+91" }) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const containerRef = useRef(null);

  const fileUrl = useMemo(() => (receipt ? buildPublicPdfUrl(receipt) : null), [receipt]);
  const filename = useMemo(() => buildReceiptFilename(receipt), [receipt]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setPage(1);
      setNumPages(null);
      setError(null);
      // mobile-default scale is smaller
      setScale(window.innerWidth < 640 ? 0.65 : 1.0);
    }
  }, [open]);

  const handleDownload = async () => {
    if (!fileUrl) return;
    try {
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Download failed. Please try again.");
    }
  };

  const openWhatsApp = (overridePhone) => {
    if (!receipt) return;
    const { waUrl, phone } = buildWhatsAppShare({
      receipt,
      defaultCountryCode,
      overridePhone,
    });
    if (!phone) {
      toast.error("Customer mobile number unavailable.");
      return;
    }
    // Synchronous open — preserves user gesture so Chrome doesn't block.
    const win = window.open(waUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.message("Pop-up was blocked", {
        description: "Click below to open WhatsApp directly.",
        action: {
          label: "Open WhatsApp",
          onClick: () => window.open(waUrl, "_blank", "noopener,noreferrer"),
        },
      });
    }
  };

  const handleShare = () => {
    if (!receipt) return;
    const recipient = receipt.snapshot?.recipient || {};
    // Apr 2026 — prefer LIVE phone numbers (current_phone / current_parent_phone
    // added by the backend at /receipts read time). Snapshot stays untouched
    // for PDF / audit integrity.
    const studentPhone = receipt.current_phone || recipient.phone || "";
    const parentPhone = receipt.current_parent_phone || recipient.parent_phone || "";
    const guardianName = receipt.current_parent_name || recipient.parent_name || "";
    if (!parentPhone && !studentPhone) {
      toast.error("Customer mobile number unavailable.");
      return;
    }
    // Single-contact case fires directly. Picker only when ≥2 valid contacts.
    const opts = buildContactOptions({
      studentName: recipient.name,
      studentPhone,
      guardianName,
      guardianPhone: parentPhone,
    });
    if (opts.length <= 1) {
      openWhatsApp(parentPhone || studentPhone);
      return;
    }
    setPickerOpen(true);
  };

  const zoomIn = () => setScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="receipt-viewer-modal"
        className="max-w-4xl w-[96vw] sm:w-[92vw] p-0 overflow-hidden gap-0"
        style={{ height: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 sm:px-5 py-3 bg-white">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold">Payment Receipt</p>
            <p className="text-sm sm:text-base font-bold text-slate-900 truncate" data-testid="viewer-receipt-number">
              {receipt?.receipt_number || "—"}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              data-testid="viewer-download-btn"
              size="sm" variant="outline"
              onClick={handleDownload}
              className="rounded-full h-9 px-2 sm:px-3"
              title="Download"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Download</span>
            </Button>
            <Button
              data-testid="viewer-share-btn"
              size="sm"
              onClick={handleShare}
              className="rounded-full h-9 px-2 sm:px-3 bg-violet-600 hover:bg-violet-700 text-white"
              title="Share via WhatsApp"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Share</span>
            </Button>
            <Button
              data-testid="viewer-close-btn"
              size="sm" variant="ghost"
              onClick={() => onOpenChange?.(false)}
              className="rounded-full h-9 w-9 p-0"
              title="Close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 sm:px-5 py-2 bg-slate-50">
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" onClick={zoomOut} className="h-8 w-8 p-0" title="Zoom out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs font-medium text-slate-600 w-10 text-center">{Math.round(scale * 100)}%</span>
            <Button size="sm" variant="ghost" onClick={zoomIn} className="h-8 w-8 p-0" title="Zoom in">
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          {numPages > 1 && (
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="h-8 w-8 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs font-medium text-slate-600">Page {page} of {numPages}</span>
              <Button size="sm" variant="ghost" onClick={() => setPage((p) => Math.min(numPages, p + 1))} disabled={page >= (numPages || 1)} className="h-8 w-8 p-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* PDF Body */}
        <div ref={containerRef} className="flex-1 overflow-auto bg-slate-200 p-3 sm:p-6 flex justify-center" data-testid="viewer-canvas-area">
          {error ? (
            <div className="self-center text-center max-w-sm">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-900">Unable to load receipt.</p>
              <p className="text-xs text-slate-500 mt-1">Please try downloading instead.</p>
              <Button
                size="sm"
                onClick={handleDownload}
                className="mt-3 rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="viewer-download-fallback"
              >
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            </div>
          ) : !fileUrl ? (
            <div className="self-center text-slate-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <Document
              file={fileUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => { console.error("PDF load error", e); setError(e); }}
              loading={
                <div className="self-center text-slate-500 text-sm flex items-center gap-2 pt-8">
                  <Loader2 className="w-4 h-4 animate-spin" /> Rendering PDF…
                </div>
              }
              error={
                <div className="self-center text-center max-w-sm pt-8">
                  <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-900">Unable to load receipt.</p>
                  <p className="text-xs text-slate-500 mt-1">Please try downloading instead.</p>
                  <Button
                    size="sm"
                    onClick={handleDownload}
                    className="mt-3 rounded-full bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> Download PDF
                  </Button>
                </div>
              }
            >
              <Page
                pageNumber={page}
                scale={scale}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                className="shadow-lg bg-white"
              />
            </Document>
          )}
        </div>
      </DialogContent>

      <ContactPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Send receipt via WhatsApp"
        description={
          receipt?.snapshot?.recipient?.name
            ? `Choose who receives the receipt for ${receipt.snapshot.recipient.name}.`
            : "Choose who receives the receipt."
        }
        options={buildContactOptions({
          studentName: receipt?.snapshot?.recipient?.name,
          studentPhone: receipt?.current_phone || receipt?.snapshot?.recipient?.phone,
          guardianName: receipt?.current_parent_name || receipt?.snapshot?.recipient?.parent_name || "",
          guardianPhone: receipt?.current_parent_phone || receipt?.snapshot?.recipient?.parent_phone,
        })}
        onPick={(key) => {
          const phone = key === "student"
            ? (receipt?.current_phone || receipt?.snapshot?.recipient?.phone)
            : (receipt?.current_parent_phone || receipt?.snapshot?.recipient?.parent_phone);
          openWhatsApp(phone);
        }}
      />
    </Dialog>
  );
};

export default ReceiptViewerModal;
